import React, { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Checkbox from "./Checkbox";

/**
 * Dropdown component - matches Input UI: rounded-xl, no shadow.
 * Supports single-select (default) and multi-select modes.
 * @see src/components/ui/Input.js
 */
const Dropdown = ({
  label,
  options = [],
  value,
  onChange,
  onBlur,
  placeholder = "Select...",
  disabled = false,
  error = false,
  className = "",
  size = "default",
  multiple = false,
  emptyLabel = "All",
  selectedCountLabel,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const closeDropdown = (callBlur = false) => {
    if (isOpen) {
      setIsOpen(false);
      if (callBlur) onBlur?.();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target) && isOpen) {
        setIsOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onBlur]);

  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : [];
  const selectedOption = !multiple && options.find((o) => o.value === value);
  const displayValue = multiple
    ? selectedValues.length === 0
      ? emptyLabel
      : selectedCountLabel
      ? selectedCountLabel(selectedValues.length)
      : `${selectedValues.length} selected`
    : selectedOption?.label ?? placeholder;

  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    default: "px-4 py-3 text-sm",
    lg: "px-4 py-3.5 text-base",
  };

  const triggerClasses = [
    "w-full flex items-center justify-between gap-2 bg-white border text-left font-medium transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary",
    sizeClasses[size],
    "rounded-xl",
    error
      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
      : "border-gray-300 hover:border-gray-400",
    disabled && "bg-gray-50 text-gray-500 cursor-not-allowed",
    !multiple && !selectedOption && "text-gray-500",
    multiple && selectedValues.length === 0 && "text-gray-500",
  ]
    .filter(Boolean)
    .join(" ");

  const handleMultiToggle = (optValue) => {
    const next = selectedValues.includes(optValue)
      ? selectedValues.filter((v) => v !== optValue)
      : [...selectedValues, optValue];
    onChange?.(next);
  };

  return (
    <div className={className} ref={ref}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-200">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          className={triggerClasses}
          onClick={() => (isOpen ? closeDropdown(false) : setIsOpen(true))}
          {...props}
        >
          <span>{displayValue}</span>
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white py-1 shadow-md max-h-60 overflow-y-auto">
            {multiple ? (
              options.map((opt) => (
                <div
                  key={opt.value}
                  className="px-4 py-2 hover:bg-gray-50 transition-colors duration-200"
                >
                  <Checkbox
                    checked={selectedValues.includes(opt.value)}
                    onChange={() => handleMultiToggle(opt.value)}
                    label={opt.label}
                  />
                </div>
              ))
            ) : (
              options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors duration-200 ${
                    opt.value === value
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    onChange?.(opt.value);
                    closeDropdown(false);
                  }}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {typeof error === "string" && (
        <p className="mt-1.5 text-xs font-medium text-red-600 animate-fadeIn">
          {error}
        </p>
      )}
    </div>
  );
};

export default Dropdown;
